export const OVERRIDE_ENV_VALUES = [
  'test7',
  'test8',
  'test9',
  'test10',
  'test11',
  'test12',
  'acc',
  'pre-prod',
] as const;

export type OverrideEnv = (typeof OVERRIDE_ENV_VALUES)[number];

export const OVERRIDE_ENV_COOKIE = 'tapp-override-env';

/** useState key holding the connected default API env label (seeded server-side). */
export const API_ENV_STATE_KEY = 'tapp-api-env';

function isOverrideEnv(value: string): value is OverrideEnv {
  for (const allowed of OVERRIDE_ENV_VALUES) {
    if (allowed === value) return true;
  }
  return false;
}

export function parseOverrideEnv(value: unknown): OverrideEnv | undefined {
  const candidate = Array.isArray(value) ? value[0] : value;
  if (typeof candidate !== 'string') return undefined;
  return isOverrideEnv(candidate) ? candidate : undefined;
}

export function getOverrideApiBaseURL(env: OverrideEnv): string {
  return `https://${env}.api.tapp.dpgmedia.cloud/front-office`;
}

/**
 * Short, human-readable env label for an API base URL — used to surface which backend the app is
 * connected to (e.g. in the inspector) without exposing the full URL.
 *
 * For the real backends the host's first subdomain label is the env itself
 * (`pre-prod`, `test7`, `acc`, `prod`). For local hosts we fall back to `host:port`.
 */
export function getApiEnvLabel(baseURL: string): string {
  try {
    const { hostname, port } = new URL(baseURL);
    if (hostname.endsWith('tapp.dpgmedia.cloud')) return hostname.split('.')[0] ?? hostname;
    return port ? `${hostname}:${port}` : hostname;
  } catch {
    return 'unknown';
  }
}

export type AuthTokenBucket = 'prod' | 'pre-prod' | 'test';

/**
 * Map an API base URL to the auth-token bucket that should authenticate it.
 *
 * Buckets follow the API host's first subdomain label:
 *   - `pre-prod`            → 'pre-prod'
 *   - `acc` or `test<n>`    → 'test'
 *   - anything else          → 'prod' (includes prod, localhost, mock)
 */
export function getAuthTokenBucket(baseURL: string): AuthTokenBucket {
  try {
    const subdomain = new URL(baseURL).hostname.split('.')[0];
    if (subdomain === 'pre-prod') return 'pre-prod';
    if (subdomain === 'acc' || /^test\d+$/.test(subdomain ?? '')) return 'test';
  } catch {
    // Fall through to the prod bucket on unparseable URLs.
  }
  return 'prod';
}
